/**
 * Reports domain — React Query hooks.
 *
 * Read hooks are re-exported directly from the generated client (all
 * verified as accurate pass-throughs against the actual wire shape — see
 * the docstrings on `ChainReportsListResponse`/`DoorReportsListResponse`/
 * `DoorReportsSummaryListResponse` in `generated/types`, which explicitly
 * model the `{results: [...]}` envelope those endpoints return, so no
 * hand-wrapping is needed there unlike `services/products`'
 * `useListVariantsCatalog`).
 *
 * `getDoorReportsSummary`'s generated response type
 * (`DoorReportsSummaryListResponse`) correctly models the `{"results": [...]}`
 * envelope (`DoorReportsSummaryView` never overrides `get()`, so it inherits
 * `DoorsWithCRMReportView`'s plain list `get()` rather than unwrapping like
 * `ChainReportsSummaryView` does). `useUnifiedDoorsReportsTable` still reads
 * the raw `getDoorReportsSummary` fetcher below rather than the generated
 * hook, for the same query-key-sharing reason as `listChainReports`/
 * `listDoorReports` below — not because the generated type is wrong.
 *
 * The deals list also gets a hand-written `useGetReportDealsInfinite`
 * wrapper (Kubb only emits single-page `useQuery` hooks for list endpoints).
 *
 * Write hooks are wrapped via `useInvalidatingMutation` so cache
 * invalidation is declarative and compile-time-enforced via the
 * `ReportsMutationId` registry in `./invalidation`.
 */

import { useInfiniteQuery } from '@tanstack/react-query'

import { useInvalidatingMutation } from '../invalidation/useInvalidatingMutation'
import { invalidationRegistry } from './invalidation'

// ── Generated query-hook re-exports (pass-through) ──────────────────

export {
  useListReportDeals,
  listReportDealsQueryKey,
  useGetReportDeal,
  getReportDealQueryKey,
  useListChainReports,
  listChainReportsQueryKey,
  useGetChainReportsSummary,
  getChainReportsSummaryQueryKey,
  useListDoorReports,
  listDoorReportsQueryKey,
  useGetDoorReportsSummary,
  getDoorReportsSummaryQueryKey,
  useGetReportsDashboardOverview,
  getReportsDashboardOverviewQueryKey,
  useGetReportsSalesForecast,
  getReportsSalesForecastQueryKey,
  useGetReportsDealsPipeline,
  getReportsDealsPipelineQueryKey,
  useGetReportsSalesPipeline,
  getReportsSalesPipelineQueryKey,
} from '../generated/hooks/reportsController'

// ── Raw fetcher re-exports (non-hook) ────────────────────────────────
//
// `DealsTableComponentV2` builds its own `useQuery` around a table-scoped
// query key (shared with several bulk-action modals' cache invalidation)
// plus a clearSelectedRows() side effect that must run on every actual
// fetch — `useListReportDeals`'s own query key/queryFn can't be threaded
// through that without losing one of the two, so it needs the underlying
// request fn directly instead of the hook.
export { listReportDeals } from '../generated/clients/listReportDeals'

// Exported as a raw fetcher (not only via a wrapped hook) because deal
// deletion reflects into the reports read-model via async CDC — callers
// must control refetch timing (delayed) rather than invalidate immediately.
// See `useBulkDealsDeleter.tsx`, the only caller.
export { bulkDeleteDealReports } from '../generated/clients/bulkDeleteDealReports'

// Exported as a raw fetcher for the same CDC reason as `bulkDeleteDealReports`
// above: the bulk-update path also reflects into the `reports/deals/` read
// model asynchronously. Unlike the delete path, the bulk-edit modals
// (`BaseSelectModal`, `AddDealsOverrideModal`, `DealsOverrideModal`) don't
// refetch at all — they patch the table cache optimistically in `onMutate`
// and never invalidate on success, so an auto-invalidating wrapped hook
// would introduce a NEW immediate invalidation that races CDC and can
// re-show pre-update rows over the correct optimistic patch. Callers adapt
// the response body via `toBulkAsyncActionResult` (`./utils`).
export { bulkUpdateDealReports } from '../generated/clients/bulkUpdateDealReports'

// `useUnifiedChainsTable`/`useUnifiedDoorsReportsTable` (`services/opportunities`)
// own their `listAction`/summary `useQuery`s around a stable
// `UNIFIED_CHAINS_SUMMARY_KEY`/`UNIFIED_DOORS_SUMMARY_KEY` query key shared with
// `commonReportsInvalidations()` — same reason as `listReportDeals` above,
// they need the raw fetchers rather than the generated hooks' own query keys.
export { listChainReports } from '../generated/clients/listChainReports'
export { listDoorReports } from '../generated/clients/listDoorReports'
export { getChainReportsSummary } from '../generated/clients/getChainReportsSummary'

export { getDoorReportsSummary } from '../generated/clients/getDoorReportsSummary'

// `SalesPipelineChart`/`DealsPipelineTable` (dashboard) each own a manual
// `useQuery` with a single stable key whose `queryFn` branches between the V1
// (products) and V2 (reports) fetch inline, mirroring `services/api`'s old
// `getSalesPipelineV2`/`getDealsPipelineV2` — same reason as `listReportDeals`
// above, they need the raw fetchers, not the generated hooks' own query keys
// (which would add a second, always-on request instead of one toggled fetch).
export { getReportsSalesPipeline } from '../generated/clients/getReportsSalesPipeline'
export { getReportsDealsPipeline } from '../generated/clients/getReportsDealsPipeline'

// ── Generated query-param type re-exports (read hooks) ──────────────

export type { ListReportDealsQueryParams } from '../generated/types/ListReportDeals'
export type { GetReportDealPathParams } from '../generated/types/GetReportDeal'
export type { ListChainReportsQueryParams } from '../generated/types/ListChainReports'
export type { GetChainReportsSummaryQueryParams } from '../generated/types/GetChainReportsSummary'
export type { ListDoorReportsQueryParams } from '../generated/types/ListDoorReports'
export type { GetDoorReportsSummaryQueryParams } from '../generated/types/GetDoorReportsSummary'
export type { GetReportsSalesForecastQueryParams } from '../generated/types/GetReportsSalesForecast'
export type { GetReportsDealsPipelineQueryParams } from '../generated/types/GetReportsDealsPipeline'

// ── Generated mutation-request/param type re-exports ────────────────

export type { ActionEnum } from '../generated/types/ActionEnum'
export type { BulkUpdateDealReportsMutationRequest } from '../generated/types/BulkUpdateDealReports'
export type { BulkDeleteDealReportsMutationRequest } from '../generated/types/BulkDeleteDealReports'
export type { ManageDealReportTagsMutationRequest } from '../generated/types/ManageDealReportTags'
export type { ManageDealReportGposMutationRequest } from '../generated/types/ManageDealReportGpos'
export type { ManageDealReportDistributorsMutationRequest } from '../generated/types/ManageDealReportDistributors'
export type { BulkDownloadDealReportSpecsheetsMutationRequest } from '../generated/types/BulkDownloadDealReportSpecsheets'
export type {
  CreateDoorReportDealsMutationRequest,
  CreateDoorReportDealsQueryParams,
} from '../generated/types/CreateDoorReportDeals'
export type {
  ManageDoorReportTagsMutationRequest,
  ManageDoorReportTagsQueryParams,
} from '../generated/types/ManageDoorReportTags'
export type {
  ManageDoorReportGposMutationRequest,
  ManageDoorReportGposQueryParams,
} from '../generated/types/ManageDoorReportGpos'
export type {
  ManageDoorReportDistributorsMutationRequest,
  ManageDoorReportDistributorsQueryParams,
} from '../generated/types/ManageDoorReportDistributors'
export type {
  CreateChainReportDealsMutationRequest,
  CreateChainReportDealsQueryParams,
} from '../generated/types/CreateChainReportDeals'
export type {
  AssignChainReportsToCampaignMutationRequest,
  AssignChainReportsToCampaignQueryParams,
} from '../generated/types/AssignChainReportsToCampaign'
export type {
  ManageChainReportTagsMutationRequest,
  ManageChainReportTagsQueryParams,
} from '../generated/types/ManageChainReportTags'
export type {
  ManageChainReportGposMutationRequest,
  ManageChainReportGposQueryParams,
} from '../generated/types/ManageChainReportGpos'
export type {
  ManageChainReportDistributorsMutationRequest,
  ManageChainReportDistributorsQueryParams,
} from '../generated/types/ManageChainReportDistributors'

// ── Internal imports for hand-written hooks + mutation wrappers ─────

import { listReportDeals } from '../generated/clients/listReportDeals'
import { manageDealReportTags } from '../generated/clients/manageDealReportTags'
import { manageDealReportGpos } from '../generated/clients/manageDealReportGpos'
import { manageDealReportDistributors } from '../generated/clients/manageDealReportDistributors'
import { bulkDownloadDealReportSpecsheets } from '../generated/clients/bulkDownloadDealReportSpecsheets'
import { createDoorReportDeals } from '../generated/clients/createDoorReportDeals'
import { manageDoorReportTags } from '../generated/clients/manageDoorReportTags'
import { manageDoorReportGpos } from '../generated/clients/manageDoorReportGpos'
import { manageDoorReportDistributors } from '../generated/clients/manageDoorReportDistributors'
import { createChainReportDeals } from '../generated/clients/createChainReportDeals'
import { assignChainReportsToCampaign } from '../generated/clients/assignChainReportsToCampaign'
import { manageChainReportTags } from '../generated/clients/manageChainReportTags'
import { manageChainReportGpos } from '../generated/clients/manageChainReportGpos'
import { manageChainReportDistributors } from '../generated/clients/manageChainReportDistributors'

import type { ListReportDealsQueryParams } from '../generated/types/ListReportDeals'
import type { ManageDealReportTagsMutationRequest } from '../generated/types/ManageDealReportTags'
import type { ManageDealReportGposMutationRequest } from '../generated/types/ManageDealReportGpos'
import type { ManageDealReportDistributorsMutationRequest } from '../generated/types/ManageDealReportDistributors'
import type { BulkDownloadDealReportSpecsheetsMutationRequest } from '../generated/types/BulkDownloadDealReportSpecsheets'
import type {
  CreateDoorReportDealsMutationRequest,
  CreateDoorReportDealsQueryParams,
} from '../generated/types/CreateDoorReportDeals'
import type {
  ManageDoorReportTagsMutationRequest,
  ManageDoorReportTagsQueryParams,
} from '../generated/types/ManageDoorReportTags'
import type {
  ManageDoorReportGposMutationRequest,
  ManageDoorReportGposQueryParams,
} from '../generated/types/ManageDoorReportGpos'
import type {
  ManageDoorReportDistributorsMutationRequest,
  ManageDoorReportDistributorsQueryParams,
} from '../generated/types/ManageDoorReportDistributors'
import type {
  CreateChainReportDealsMutationRequest,
  CreateChainReportDealsQueryParams,
} from '../generated/types/CreateChainReportDeals'
import type {
  AssignChainReportsToCampaignMutationRequest,
  AssignChainReportsToCampaignQueryParams,
} from '../generated/types/AssignChainReportsToCampaign'
import type {
  ManageChainReportTagsMutationRequest,
  ManageChainReportTagsQueryParams,
} from '../generated/types/ManageChainReportTags'
import type {
  ManageChainReportGposMutationRequest,
  ManageChainReportGposQueryParams,
} from '../generated/types/ManageChainReportGpos'
import type {
  ManageChainReportDistributorsMutationRequest,
  ManageChainReportDistributorsQueryParams,
} from '../generated/types/ManageChainReportDistributors'

// ── Hand-written query hooks ─────────────────────────────────────────

/**
 * Infinite-query wrapper around the generated `listReportDeals` client fn.
 *
 * Kubb only emits single-page `useQuery` hooks for list endpoints, so this
 * mirrors the deals table's paging need directly. The query key
 * (`['report-deals', ...]`) intentionally matches the legacy
 * `api.useGetReportDealsInfinite` key so both caches interop during the
 * migration — see `commonReportsInvalidations` in `./invalidation`.
 */
export function useGetReportDealsInfinite(
  params: ListReportDealsQueryParams = {}
) {
  return useInfiniteQuery({
    queryKey: ['report-deals', JSON.stringify(params)],
    initialPageParam: 1,
    queryFn: ({ pageParam }) =>
      listReportDeals({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      return totalFetched < lastPage.count ? allPages.length + 1 : undefined
    },
  })
}

// ── Mutation hooks with auto-invalidation (WRITE) ───────────────────
//
// CAVEAT — currently unconsumed. None of the hooks below are wired up to a
// component yet; the real table actions (manage tags/gpos/distributors,
// create-deals-from-selection, assign-to-campaign) route through the raw
// fetchers in `services/common_table_actions.ts` instead.
//
// 12 of these 13 hooks (every one except `useBulkDownloadDealReportSpecsheets`,
// annotated separately below) wrap CDC-sensitive WRITES: the underlying
// request reflects into the reports read-model asynchronously via CDC, same
// as `bulkUpdateDealReports`/`bulkDeleteDealReports` re-exported above.
// `useInvalidatingMutation`'s auto-invalidation fires immediately on
// success, which would race that async pipeline — the exact bug that forced
// bulk delete/update to become raw fetchers + a delayed manual refetch (see
// the `bulkDeleteDealReports` re-export's doc comment above and
// `useBulkDealsDeleter.tsx`, its only caller). If a future consumer needs
// this write's effect to be immediately visible in a table/summary, follow
// that precedent — raw fetcher + delayed refetch — instead of reaching for
// one of these wrapped hooks as-is.
//
// Kept as forward-looking scaffolding, not dead code to delete: the
// compiler-enforced `ReportsMutationId` registry (`./invalidation`) requires
// a 1:1 entry per mutation regardless of whether a hook consumes it yet.

export function useManageDealReportTags() {
  return useInvalidatingMutation('manageDealReportTags', invalidationRegistry, {
    mutationFn: (variables: { data: ManageDealReportTagsMutationRequest }) =>
      manageDealReportTags(variables.data),
  })
}

export function useManageDealReportGpos() {
  return useInvalidatingMutation('manageDealReportGpos', invalidationRegistry, {
    mutationFn: (variables: { data: ManageDealReportGposMutationRequest }) =>
      manageDealReportGpos(variables.data),
  })
}

export function useManageDealReportDistributors() {
  return useInvalidatingMutation(
    'manageDealReportDistributors',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ManageDealReportDistributorsMutationRequest
      }) => manageDealReportDistributors(variables.data),
    }
  )
}

// Read-only side effect (kicks off an async specsheet-generation job; writes
// nothing to reports data) — the CDC-race caveat above doesn't apply here.
// Wrapped only for registry completeness, per the `invalidates: () => []`
// entry in `./invalidation`.
export function useBulkDownloadDealReportSpecsheets() {
  return useInvalidatingMutation(
    'bulkDownloadDealReportSpecsheets',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data?: BulkDownloadDealReportSpecsheetsMutationRequest
      }) => bulkDownloadDealReportSpecsheets(variables.data),
    }
  )
}

export function useCreateDoorReportDeals() {
  return useInvalidatingMutation(
    'createDoorReportDeals',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: CreateDoorReportDealsMutationRequest
        params?: CreateDoorReportDealsQueryParams
      }) => createDoorReportDeals(variables.data, variables.params),
    }
  )
}

export function useManageDoorReportTags() {
  return useInvalidatingMutation('manageDoorReportTags', invalidationRegistry, {
    mutationFn: (variables: {
      data: ManageDoorReportTagsMutationRequest
      params?: ManageDoorReportTagsQueryParams
    }) => manageDoorReportTags(variables.data, variables.params),
  })
}

export function useManageDoorReportGpos() {
  return useInvalidatingMutation('manageDoorReportGpos', invalidationRegistry, {
    mutationFn: (variables: {
      data: ManageDoorReportGposMutationRequest
      params?: ManageDoorReportGposQueryParams
    }) => manageDoorReportGpos(variables.data, variables.params),
  })
}

export function useManageDoorReportDistributors() {
  return useInvalidatingMutation(
    'manageDoorReportDistributors',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ManageDoorReportDistributorsMutationRequest
        params?: ManageDoorReportDistributorsQueryParams
      }) => manageDoorReportDistributors(variables.data, variables.params),
    }
  )
}

export function useCreateChainReportDeals() {
  return useInvalidatingMutation(
    'createChainReportDeals',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: CreateChainReportDealsMutationRequest
        params?: CreateChainReportDealsQueryParams
      }) => createChainReportDeals(variables.data, variables.params),
    }
  )
}

export function useAssignChainReportsToCampaign() {
  return useInvalidatingMutation(
    'assignChainReportsToCampaign',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: AssignChainReportsToCampaignMutationRequest
        params?: AssignChainReportsToCampaignQueryParams
      }) => assignChainReportsToCampaign(variables.data, variables.params),
    }
  )
}

export function useManageChainReportTags() {
  return useInvalidatingMutation(
    'manageChainReportTags',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ManageChainReportTagsMutationRequest
        params?: ManageChainReportTagsQueryParams
      }) => manageChainReportTags(variables.data, variables.params),
    }
  )
}

export function useManageChainReportGpos() {
  return useInvalidatingMutation(
    'manageChainReportGpos',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ManageChainReportGposMutationRequest
        params?: ManageChainReportGposQueryParams
      }) => manageChainReportGpos(variables.data, variables.params),
    }
  )
}

export function useManageChainReportDistributors() {
  return useInvalidatingMutation(
    'manageChainReportDistributors',
    invalidationRegistry,
    {
      mutationFn: (variables: {
        data: ManageChainReportDistributorsMutationRequest
        params?: ManageChainReportDistributorsQueryParams
      }) => manageChainReportDistributors(variables.data, variables.params),
    }
  )
}
